import { DataType } from "@/components/DataTable/Models";
import { IconTable } from "@codexteam/icons";
import { API, BlockAPI, BlockTool } from "@editorjs/editorjs";
import { BlockToolConstructorOptions, BlockToolData } from "@editorjs/editorjs/types/tools";
import { createRoot } from "react-dom/client";
import { DocumentationTableColumnData } from "../DocumentationTableBlock/DocumentationTableBlockTool";
import { GeometryTableBlockComponent } from "./GeometryTableBlockComponent";

export interface GeometryTableBlockData extends BlockToolData {
    title?: string;
    caption?: string;
    columns: DocumentationTableColumnData[];
}

export interface GeometryTableBlockConfig {
    currentLanguage: string;
}

const DEFAULT_CONFIG: { [key: string]: GeometryTableBlockData } = {
    "int-en": {
        title: "Geometry level",
        columns: [
            {
                value: "Element",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\n1xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n2xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n3xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n4xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n5xx",
                dataType: DataType.BOOLEAN,
            },
        ],
    },

    "int-fr": {
        title: "NIVEAU DE GEOMETRIE",
        columns: [
            {
                value: "Elément",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\n1xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n2xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n3xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n4xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n5xx",
                dataType: DataType.BOOLEAN,
            },
        ],
    },

    "int-nl": {
        title: "Geometrisch niveau",
        columns: [
            {
                value: "Element",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\n1xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n2xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n3xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n4xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n5xx",
                dataType: DataType.BOOLEAN,
            },
        ],
    },

    "int-de": {
        title: "Geometry level",
        columns: [
            {
                value: "Element",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\n1xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n2xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n3xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n4xx",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\n5xx",
                dataType: DataType.BOOLEAN,
            },
        ],
    },
};

export class GeometryTableBlockTool implements BlockTool {
    public static get toolbox() {
        return {
            title: "GID - Geometry",
            icon: IconTable,
        };
    }

    api: API;
    block?: BlockAPI;
    config: GeometryTableBlockData;
    data: GeometryTableBlockData;
    currentData: GeometryTableBlockData;
    CSS: { [key: string]: string };
    nodes: { [key: string]: HTMLElement | null };

    // Use `any` to circumvent an issue with Editor.js when using TypeScript
    constructor(params: BlockToolConstructorOptions<GeometryTableBlockData, GeometryTableBlockConfig>) {
        const { api, block, config, data } = params;
        if (!config) throw new Error("GeometryTableBlockTool configuration missing");

        this.api = api;
        this.block = block;
        this.data = data;
        this.config = DEFAULT_CONFIG[config.currentLanguage]!;

        if (data && Object.keys(data).length > 0) {
            this.data = data;
        } else {
            this.data = JSON.parse(JSON.stringify(DEFAULT_CONFIG[config.currentLanguage]!));
        }

        this.currentData = JSON.parse(JSON.stringify(this.data));

        this.CSS = {
            wrapper: "template-editor-content-block",
        };

        this.nodes = {
            holder: null,
            table: null,
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    save(block: HTMLElement) {
        return {
            title: this.api.sanitizer.clean(this.currentData.title || "", {}),
            columns: this.currentData.columns.map((column) => ({
                ...column,
                value: this.api.sanitizer.clean(column.value || "", {}),
            })),
            caption: this.api.sanitizer.clean(this.currentData.caption || "", {}),
        };
    }

    render(): HTMLElement {
        if (!this.nodes.table) {
            const rootNode = document.createElement("div");
            rootNode.setAttribute("class", this.CSS.wrapper);
            this.nodes.table = rootNode;

            const root = createRoot(rootNode);
            root.render(
                <GeometryTableBlockComponent
                    data={this.data}
                    defaults={this.config}
                    onChange={(data) => {
                        this.currentData = data;
                        this.block?.dispatchChange();
                    }}
                />
            );
        }

        if (!this.nodes.holder) {
            const holderNode = document.createElement("div");
            holderNode.setAttribute("class", this.CSS.wrapper);
            holderNode.appendChild(this.nodes.table);
            this.nodes.holder = holderNode;
        }

        return this.nodes.holder;
    }
}
